import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Table, Form, Button, Badge, Alert, Spinner } from "react-bootstrap";
import moment from "moment";
import { getCartePreview, getEstablishments } from "./api";
import { mergeOffers, describeDifferences, rowKey, rowAnchorId, annotateMergeKeys } from "./compare";

/**
 * « À la carte » — ce que le client du tunnel B2C verra, et ce qui ne sortira pas.
 *
 * La vue est comparative d'emblée : les deux établissements sont chargés côte à
 * côte pour la même date et le même canal, chacun dans le MÊME tableau (photo,
 * produit, stock, statut, raison). Au-dessus, le récapitulatif des écarts —
 * c'est lui qui dit où regarder, les deux tableaux servent ensuite à vérifier le
 * détail sans changer d'écran.
 *
 * Vue strictement en LECTURE : aucun appel d'écriture, aucune notion de panier.
 *
 * Les raisons viennent du serveur sous forme de CODES, calculés par la MÊME
 * passe de disponibilité que l'offre publique (OfferPreviewBuilder côté
 * lemoulinapi) — un outil de contrôle qui réimplémenterait les règles qu'il
 * contrôle finirait par afficher autre chose que la réalité. Ici on ne fait que
 * traduire ces codes.
 */

const REASON_LABELS = {
  genericInactive: "Produit générique désactivé",
  productInactive: "Produit désactivé sur ce site",
  notOnShop: "Non activé en boutique",
  notOnSpot: "Non activé en charrette",
  notOnAnyChannel: "Activé sur aucun canal",
  hiddenFromHour: "Masqué à partir de {time}",
  notThisDay: "Pas au menu ce jour-là",
  hiddenForSuggestions: "Masqué au profit de ses suggestions",
  suggestionUnavailable: "Suggestion indisponible ce jour-là",
  suggestionNotOnShop: "Suggestion non activée en boutique",
  suggestionNotOnSpot: "Suggestion non activée en charrette",
  suggestionNotOnAnyChannel: "Suggestion activée sur aucun canal",
  suggestionNoPeriod: "Aucune période de disponibilité définie",
  suggestionOutOfPeriod: "Hors de sa période de disponibilité",
  suggestionIngredientUnavailable: "Ingrédient indisponible : {names}",
  // Suivi des raisons du parent lui-même : « Produit sur-mesure parent non
  // affiché · Pas au menu ce jour-là ».
  suggestionParentHidden: "Produit sur-mesure parent non affiché",
  unknown: "Raison non identifiée",
  categoryNotOnShop: "Catégorie non activée en boutique",
  categoryNotOnSpot: "Catégorie non activée en charrette",
  categoryNotOnAnyChannel: "Catégorie activée sur aucun canal",
  categoryPreorderOnly: "Catégorie en précommande uniquement",
  categoryNoVisibleProduct: "Aucun de ses {count} produits n'est affichable",
  sectionNoVisibleCategory: "Aucune catégorie visible",
};

const STATUS_LABELS = {
  orderable: "En carte",
  soldOut: "Rupture",
  hidden: "Non affiché",
};

const STATUS_VARIANTS = {
  orderable: "success",
  soldOut: "warning",
  hidden: "secondary",
};

const DIFF_BACKGROUND = "#fff8e1";

/**
 * Un code inconnu s'affiche tel quel plutôt que de disparaître : si l'API en
 * ajoute un, il doit se voir à l'écran, pas se perdre en silence.
 */
function reasonLabel(reason) {
  const template = REASON_LABELS[reason.code];
  if (!template) return reason.code;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    reason.params && reason.params[key] !== undefined ? reason.params[key] : match
  );
}

const reasonsLabel = (reasons) => (reasons || []).map(reasonLabel).join(" · ");

// Accents retirés des deux côtés : on cherche « pate » et on veut « pâtes ».
const normalize = (s) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/**
 * `stockKnown` distingue « stock illimité » de « stock jamais calculé » : la
 * passe de disponibilité ne chiffre le stock qu'une fois les contrôles
 * d'activation passés. Afficher 0 pour un produit simplement désactivé
 * annoncerait une rupture qui n'existe pas.
 */
function stockLabel(row) {
  if (!row || !row.stockKnown) return "—";
  // Stock non plafonné : le nombre de ventes reste l'information utile.
  if (row.stockInitial === null || row.stockInitial === undefined) {
    return row.sold ? `${row.sold} vendu${row.sold > 1 ? "s" : ""}` : "Illimité";
  }
  return `${row.sold ?? 0}/${row.stockInitial}`;
}

const priceLabel = (cents) =>
  cents === null || cents === undefined
    ? "—"
    : (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

/**
 * Tri par prix croissant, appliqué À L'INTÉRIEUR d'un bloc de sous-catégorie.
 *
 * C'est exactement ce que fait le tunnel B2C (ProductGrid : prix croissant dans
 * le bloc affiché, donc par sous-catégorie quand la catégorie en a). Les
 * produits sans prix passent en dernier plutôt que de remonter en tête comme le
 * ferait un 0 ; à prix égal, l'alphabétique évite que l'ordre des ex æquo bouge
 * d'un rechargement à l'autre — ça saute aux yeux quand on compare deux colonnes.
 */
function byPrice(a, b) {
  const pa = a.priceCents === null || a.priceCents === undefined ? Infinity : a.priceCents;
  const pb = b.priceCents === null || b.priceCents === undefined ? Infinity : b.priceCents;
  if (pa !== pb) return pa - pb;
  return (a.name || "").localeCompare(b.name || "", "fr");
}

/** Ancre d'une section, propre à une colonne. */
const anchorId = (side, sectionId) => `cp-${side}-s${sectionId}`;

const boUrl = process.env.REACT_APP_LEMOULIN_BO_URL;

/**
 * Fiches Sonata d'un produit — deux niveaux, comme les boutons « Edit Etab » /
 * « Edit Gen » de l'ancien tunnel :
 *  - le produit ÉTABLISSEMENT porte ce qui explique la plupart des absences
 *    (activation par canal, prix, stock) — c'est la fiche utile par défaut ;
 *  - le produit GÉNÉRIQUE porte ce qui vaut pour tous les sites (activation
 *    globale, jours de disponibilité, photo).
 *
 * Une ligne « suggestion » n'a pas de fiche propre : les suggestions se règlent
 * sur la fiche du produit sur-mesure parent, dont `row.id` est justement l'id.
 */
const productEditUrl = (id) => `${boUrl}/e28XmZh3/app/store-v2-product-product/${id}/edit`;
const genericEditUrl = (genericId) => `${boUrl}/e28XmZh3/app/product/${genericId}/edit`;

/**
 * Sommaire de navigation : une entrée par section, construit à partir des DEUX
 * colonnes.
 *
 * Le rapprochement se fait sur les noms : une section appartient à un
 * établissement, les identifiants ne se recoupent pas d'un site à l'autre.
 * Chaque entrée retient l'ancre de chaque colonne — une section qui n'existe que
 * sur le site de droite reste ainsi atteignable.
 */
function buildNav(sectionsA, sectionsB) {
  const sections = new Map();

  const visit = (list, side) => {
    list.forEach((section) => {
      if (!sections.has(section.name)) sections.set(section.name, { name: section.name, idA: null, idB: null });
      sections.get(section.name)[side === "a" ? "idA" : "idB"] = anchorId(side, section.id);
    });
  };

  visit(sectionsA, "a");
  visit(sectionsB, "b");

  return Array.from(sections.values());
}

/**
 * Découpe les lignes d'une catégorie en blocs de sous-catégorie, dans l'ordre du
 * back-office — le même empilement que la carte du client.
 *
 * L'ordre vient de `underCategorySort` envoyé par l'API, pas de l'ordre
 * d'arrivée du tableau : on trie les lignes par prix juste après, ce qui ne
 * survivrait pas à un regroupement implicite. `-1` = produits rattachés
 * directement à la catégorie, qui passent avant les blocs nommés.
 */
function groupByUnderCategory(rows) {
  const blocks = new Map();

  rows.forEach((row) => {
    const key = row.underCategoryId ?? "direct";
    if (!blocks.has(key)) {
      blocks.set(key, {
        key,
        name: row.underCategory || null,
        sort: row.underCategorySort === undefined ? -1 : row.underCategorySort,
        rows: [],
      });
    }
    blocks.get(key).rows.push(row);
  });

  return Array.from(blocks.values())
    .sort((a, b) => (a.sort !== b.sort ? a.sort - b.sort : (a.name || "").localeCompare(b.name || "", "fr")))
    .map((block) => ({ ...block, rows: block.rows.sort(byPrice) }));
}

/**
 * Un 401 ici n'est presque jamais un problème de cette vue : c'est la session BO
 * qui a expiré, et TOUS les appels de l'admin échouent en même temps. Le dire
 * évite de chercher la panne du mauvais côté. Le 403, lui, est spécifique :
 * compte connecté mais sans le rôle.
 */
function errorMessage(e, fallback) {
  const status = e.response?.status;
  if (status === 401) return "Session expirée — reconnectez-vous au back-office.";
  if (status === 403) return "Accès réservé aux comptes administrateurs.";
  return e.response?.data?.result?.message || e.response?.data?.message || fallback;
}

const CartePreview = ({ establishment }) => {
  const [establishments, setEstablishments] = useState([]);
  const [siteA, setSiteA] = useState(establishment || null);
  const [siteB, setSiteB] = useState(null);
  const [channel, setChannel] = useState("spot");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));

  const [offerA, setOfferA] = useState(null);
  const [offerB, setOfferB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState("visible");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    getEstablishments()
      .then((list) => {
        if (cancelled) return;
        const sites = list || [];
        setEstablishments(sites);
        // Le site du BO (session) sert de défaut à gauche ; le suivant à droite.
        setSiteA((current) => current || (sites.length ? sites[0].id : null));
        setSiteB((current) => {
          if (current) return current;
          const first = sites.find((s) => s.id !== (establishment || (sites[0] && sites[0].id)));
          return first ? first.id : sites.length > 1 ? sites[1].id : null;
        });
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e, "Impossible de charger la liste des sites."));
      });
    return () => {
      cancelled = true;
    };
  }, [establishment]);

  const siteName = useCallback(
    (id) => establishments.find((e) => e.id === id)?.name || "—",
    [establishments]
  );

  const load = useCallback(() => {
    if (!siteA || !siteB) return undefined;

    setLoading(true);
    setError(null);
    let cancelled = false;

    // Les deux sites partent EN PARALLÈLE : la comparaison doit porter sur le
    // même instant, et deux appels enchaînés doubleraient l'attente.
    Promise.all(
      [siteA, siteB].map((id) => getCartePreview({ establishment: id, date, for: channel }))
    )
      .then(([a, b]) => {
        if (cancelled) return;
        // Clés de rapprochement posées ICI, sur la réponse brute : le rang
        // d'occurrence qu'elles portent n'a de sens que dans l'ordre d'origine,
        // avant que le filtre et le tri par prix ne réorganisent les lignes.
        setOfferA(annotateMergeKeys(a));
        setOfferB(annotateMergeKeys(b));
      })
      .catch((e) => {
        if (cancelled) return;
        setOfferA(null);
        setOfferB(null);
        setError(errorMessage(e, "Impossible de charger la carte."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [siteA, siteB, date, channel]);

  // Toute bascule de contexte recharge : c'est le cœur de l'outil (« voir la
  // carte d'un autre site »), donc pas de bouton « Appliquer » à cliquer en plus.
  useEffect(() => load(), [load]);

  const merged = useMemo(
    () => (offerA || offerB ? mergeOffers(offerA, offerB) : []),
    [offerA, offerB]
  );

  // Le total affiché est celui des puces réellement listées : un compteur qui
  // annoncerait 20 écarts au-dessus de 6 lignes ferait chercher les 14 autres.
  const differences = useMemo(() => describeDifferences(merged), [merged]);

  /**
   * Clés des lignes qui divergent : elles surlignent les mêmes produits DANS LES
   * DEUX tableaux. Sans ça, deux listes côte à côte se décalent dès qu'un
   * produit manque d'un côté et l'écart devient invisible à l'œil.
   */
  const diffKeys = useMemo(() => {
    const keys = new Set();
    merged.forEach((section) =>
      section.categories.forEach((category) =>
        category.rows.forEach((row) => {
          if (row.differs) keys.add(row.key);
        })
      )
    );
    return keys;
  }, [merged]);

  /**
   * Filtre appliqué à chaque carte indépendamment. Une catégorie vidée par le
   * filtre disparaît (et sa section avec, si elle se vide) : sans ça, la moitié
   * de l'écran est occupée par des titres sans lignes.
   */
  const filterOffer = useCallback(
    (offer) => {
      const q = normalize(search.trim());
      const sections = [];

      (offer?.sections || []).forEach((section) => {
        const categories = [];
        section.categories.forEach((category) => {
          const products = category.products.filter((p) => {
            if (mode === "visible" && p.status === "hidden") return false;
            if (mode === "hidden" && p.status !== "hidden") return false;
            return !q || normalize(p.name).includes(q);
          });
          // Blocs de sous-catégorie dans l'ordre du BO, prix croissant dedans :
          // même empilement que la carte du client.
          if (products.length) {
            categories.push({ ...category, blocks: groupByUnderCategory(products) });
          }
        });
        if (categories.length) sections.push({ ...section, categories });
      });

      return sections;
    },
    [mode, search]
  );

  const sectionsA = useMemo(() => filterOffer(offerA), [filterOffer, offerA]);
  const sectionsB = useMemo(() => filterOffer(offerB), [filterOffer, offerB]);
  const nav = useMemo(() => buildNav(sectionsA, sectionsB), [sectionsA, sectionsB]);

  return (
    <div style={{ padding: "1rem 0" }}>
      <Form style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "1.5rem", marginBottom: "1rem" }}>
        <div>
          <FieldLabel>Sites comparés</FieldLabel>
          <div style={{ display: "flex", gap: ".35rem", alignItems: "center" }}>
            <Form.Control
              as="select"
              size="sm"
              value={siteA || ""}
              onChange={(e) => setSiteA(Number(e.target.value))}
              style={{ width: "11rem" }}
            >
              {establishments.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Form.Control>
            <span style={{ color: "#999" }}>vs</span>
            <Form.Control
              as="select"
              size="sm"
              value={siteB || ""}
              onChange={(e) => setSiteB(Number(e.target.value))}
              style={{ width: "11rem" }}
            >
              {establishments.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div>
          <FieldLabel>Canal</FieldLabel>
          <div style={{ display: "flex", gap: ".35rem" }}>
            <Button
              size="sm"
              variant={channel === "spot" ? "success" : "outline-secondary"}
              onClick={() => setChannel("spot")}
            >
              Charrette
            </Button>
            <Button
              size="sm"
              variant={channel === "shop" ? "success" : "outline-secondary"}
              onClick={() => setChannel("shop")}
            >
              Boutique
            </Button>
          </div>
        </div>

        <div>
          <FieldLabel>Date</FieldLabel>
          <div style={{ display: "flex", gap: ".35rem", alignItems: "center" }}>
            <Form.Control
              type="date"
              size="sm"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ width: "10rem" }}
            />
            <Button size="sm" variant="link" onClick={() => setDate(moment().format("YYYY-MM-DD"))}>
              Aujourd'hui
            </Button>
          </div>
        </div>

        <div>
          <FieldLabel>Afficher</FieldLabel>
          <div style={{ display: "flex", gap: ".35rem" }}>
            {[
              { key: "visible", label: "La carte du jour" },
              { key: "hidden", label: "Uniquement les absents" },
              { key: "all", label: "Tout" },
            ].map((m) => (
              <Button
                key={m.key}
                size="sm"
                variant={mode === m.key ? "success" : "outline-secondary"}
                onClick={() => setMode(m.key)}
              >
                {m.label}
              </Button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: "12rem" }}>
          <FieldLabel>Rechercher</FieldLabel>
          <Form.Control
            type="search"
            size="sm"
            placeholder="Nom du produit…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div>
          <FieldLabel>&nbsp;</FieldLabel>
          {/* Rechargement manuel : stock et ventes bougent en continu pendant le
              service, et la vue n'est PAS rafraîchie toute seule (un rechargement
              périodique déplacerait les lignes sous le curseur en pleine lecture).
              `type="button"` : dans un <Form>, le défaut est "submit" — un clic
              rechargerait la page entière. */}
          <Button
            type="button"
            size="sm"
            variant="outline-success"
            disabled={loading}
            onClick={() => load()}
            title="Recharger les deux cartes"
          >
            {loading ? "Chargement…" : "↻ Actualiser"}
          </Button>
        </div>
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {siteA === siteB && <Alert variant="warning">Les deux sélecteurs pointent le même site.</Alert>}

      {!loading && (offerA || offerB) && (
        <DiffSummary differences={differences} nameA={siteName(siteA)} nameB={siteName(siteB)} />
      )}

      {!loading && nav.length > 0 && <NavBar nav={nav} />}

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : (
        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
          <SiteColumn
            side="a"
            name={siteName(siteA)}
            counts={offerA?._meta?.counts}
            sections={sectionsA}
            diffKeys={diffKeys}
          />
          <SiteColumn
            side="b"
            name={siteName(siteB)}
            counts={offerB?._meta?.counts}
            sections={sectionsB}
            diffKeys={diffKeys}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Nom du produit dans le récap : un clic amène à sa ligne dans les tableaux.
 *
 * On vise la colonne de GAUCHE, avec repli sur celle de droite quand le produit
 * n'est à la carte que là (« pas à la carte de… »). `block: "center"` plutôt que
 * "start" : la ligne se pose au milieu de l'écran, où l'on voit aussi ses
 * voisines — atterrir tout en haut du cadre ne dit pas dans quel bloc on est.
 *
 * Bouton et non lien : il n'y a pas d'URL à ouvrir, et un <a href="#..."> aurait
 * empilé des entrées dans l'historique du navigateur à chaque écart consulté.
 */
const DiffName = ({ diff }) => {
  const scrollTo = () => {
    // Sur un déplacement, les deux côtés ne sont pas dans la même catégorie :
    // l'ancre de chacun se calcule avec la sienne.
    const target =
      (diff.a && document.getElementById(rowAnchorId("a", diff.categoryA || diff.category, diff.a))) ||
      (diff.b && document.getElementById(rowAnchorId("b", diff.categoryB || diff.category, diff.b)));
    if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <button
      type="button"
      onClick={scrollTo}
      title="Voir la ligne dans le tableau"
      style={{
        background: "none",
        border: "none",
        padding: 0,
        font: "inherit",
        fontWeight: 700,
        color: "#212529",
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        cursor: "pointer",
      }}
    >
      {diff.name}
    </button>
  );
};

/**
 * Accès direct aux fiches Sonata depuis une puce du récap : le générique, puis
 * la fiche de chaque site.
 *
 * Le produit établissement n'a PAS le même identifiant d'un site à l'autre —
 * chaque lien porte donc celui de son propre côté, pris sur la ligne
 * correspondante. Un côté sans ligne n'a pas de fiche à ouvrir : pas de lien,
 * plutôt qu'un lien mort. Les puces catégorie n'en ont aucun, elles ne
 * désignent pas un produit.
 */
const EditLinks = ({ diff, nameA, nameB }) => {
  const AVEC_FICHES = ["rowStatus", "rowMissing", "rowMovedCategory", "rowMovedUnderCategory"];
  if (!AVEC_FICHES.includes(diff.type)) return null;

  const genericId = (diff.a && diff.a.genericId) || (diff.b && diff.b.genericId);
  const linkStyle = { marginLeft: ".45rem", fontSize: ".75rem" };
  // Une suggestion n'a pas de fiche propre : le lien mène à son produit parent.
  const parentNote = diff.kind === "suggestion" ? " (fiche du produit sur-mesure parent)" : "";

  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span style={{ color: "#ddd", marginLeft: ".45rem" }}>|</span>
      {genericId && (
        <a
          href={genericEditUrl(genericId)}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          title="Fiche du produit générique (tous sites)"
        >
          gén.
        </a>
      )}
      {diff.a && (
        <a
          href={productEditUrl(diff.a.id)}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          title={`Fiche ${nameA}${parentNote}`}
        >
          {nameA}
        </a>
      )}
      {diff.b && (
        <a
          href={productEditUrl(diff.b.id)}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          title={`Fiche ${nameB}${parentNote}`}
        >
          {nameB}
        </a>
      )}
    </span>
  );
};

/**
 * Raison d'un écart, en clair.
 *
 * Le côté masqué porte les codes ; c'est lui qu'on interroge. Une rupture n'a
 * pas de code (elle n'est pas « masquée »), son libellé est donc écrit ici.
 * Renvoie null quand il n'y a rien de plus à dire que les deux statuts.
 */
function diffReason(d) {
  if (d.type === "rowMissing") return null;
  if (d.type !== "rowStatus") return null;

  const hidden = d.a.status === "hidden" ? d.a : d.b.status === "hidden" ? d.b : null;
  if (hidden) return reasonsLabel(hidden.reasons);
  if (d.a.status === "soldOut" || d.b.status === "soldOut") return "Stock épuisé";
  return null;
}

/**
 * Récapitulatif des écarts, en puces.
 *
 * Ne liste que ce que le CLIENT perçoit (filtrage fait par describeDifferences) :
 * deux réglages qui divergent sans qu'aucune carte ne change n'ont rien à faire
 * ici. Le reste se lit dans les tableaux, filtre « Tout ».
 *
 * Liste TOUT le reste — pas de troncature silencieuse : un « et 40 autres »
 * masqué donnerait l'impression d'avoir tout vu. Au-delà d'une quinzaine de
 * lignes le bloc défile sur lui-même plutôt que de repousser les tableaux hors
 * de l'écran.
 */
const DiffSummary = ({ differences, nameA, nameB }) => {
  // Déclaré AVANT le retour anticipé « aucun écart » : un hook placé après
  // serait appelé de façon conditionnelle, et React refuserait le rendu dès la
  // première comparaison sans écart.
  const [open, setOpen] = useState(false);

  if (!differences.length) {
    return (
      <Alert variant="success" style={{ padding: ".5rem .75rem" }}>
        <strong>Aucun écart sur la carte du jour</strong> — les deux sites montrent les mêmes produits, dans les mêmes états.
      </Alert>
    );
  }

  const sideName = (side) => (side === "a" ? nameA : nameB);
  const otherName = (side) => (side === "a" ? nameB : nameA);
  const total = differences.length;

  return (
    <div style={{ background: "#fff", border: "1px solid #ffe0a3", borderLeft: "4px solid #f0ad4e", borderRadius: 6, padding: ".75rem 1rem", marginBottom: "1rem" }}>
      {/* Replié par défaut : le compte suffit à savoir s'il y a lieu de
          regarder, et la liste dépliée repoussait les deux tableaux hors de
          l'écran à chaque chargement. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          fontWeight: 600,
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
        }}
      >
        <span style={{ display: "inline-block", width: "1rem", color: "#f0ad4e" }}>{open ? "▾" : "▸"}</span>
        {total} écart{total > 1 ? "s" : ""} visible{total > 1 ? "s" : ""} sur la carte du jour entre {nameA} et {nameB}
      </button>
      {open && (
      <ul style={{ margin: ".4rem 0 0", paddingLeft: "1.1rem", maxHeight: "18rem", overflowY: "auto", fontSize: ".85rem" }}>
        {differences.map((d) => {
          const reason = diffReason(d);
          return (
            <li key={d.key} style={{ marginBottom: ".15rem" }}>
              {d.type === "categoryMissing" && (
                <>
                  Catégorie <strong>{d.category}</strong> — présente uniquement sur {sideName(d.onlySide)}
                  {d.rowCount > 0 && `, avec ses ${d.rowCount} produit${d.rowCount > 1 ? "s" : ""} en carte`}
                </>
              )}
              {d.type === "categoryHidden" && (
                <>
                  Catégorie <strong>{d.category}</strong> — affichée seulement sur {sideName(d.onlySide)}
                </>
              )}
              {d.type === "rowMissing" && (
                <>
                  <DiffName diff={d} />
                  {d.kind === "suggestion" && " (suggestion)"} — {STATUS_LABELS[(d.a || d.b).status]} sur{" "}
                  {/* « Pas à la carte » et non « absent du catalogue » : le
                      produit établissement existe en général des deux côtés, il
                      n'est simplement rattaché à aucune sous-catégorie d'une
                      section sur ce site — donc invisible pour le client. Dire
                      « absent » envoyait chercher un produit qui existe. */}
                  {sideName(d.onlySide)}, pas à la carte de {otherName(d.onlySide)}
                </>
              )}
              {d.type === "rowStatus" && (
                <>
                  <DiffName diff={d} />
                  {d.kind === "suggestion" && " (suggestion)"} — {STATUS_LABELS[d.a.status]} sur {nameA},{" "}
                  {STATUS_LABELS[d.b.status]} sur {nameB}
                </>
              )}
              {/* Rangé ailleurs : le produit est bien à la carte des deux côtés,
                  mais le client ne le cherche pas au même endroit. */}
              {d.type === "rowMovedCategory" && (
                <>
                  <DiffName diff={d} />
                  {d.kind === "suggestion" && " (suggestion)"} — dans « {d.categoryA} » sur {nameA},
                  {" "}« {d.categoryB} » sur {nameB}
                </>
              )}
              {d.type === "rowMovedUnderCategory" && (
                <>
                  <DiffName diff={d} />
                  {d.kind === "suggestion" && " (suggestion)"} — sous « {d.a.underCategory || "aucune sous-catégorie"} »
                  {" "}sur {nameA}, « {d.b.underCategory || "aucune sous-catégorie"} » sur {nameB}
                </>
              )}
              {/* La raison à côté de l'écart : la colonne « Raison » ayant quitté
                  les tableaux, c'est ici qu'elle doit se lire. */}
              {reason && <span style={{ color: "#b26a00" }}> → {reason}</span>}
              {d.category && (d.type === "rowMissing" || d.type === "rowStatus" || d.type === "rowMovedUnderCategory") && (
                <em style={{ color: "#aaa" }}> · {d.category}</em>
              )}
              <EditLinks diff={d} nameA={nameA} nameB={nameB} />
            </li>
          );
        })}
      </ul>
      )}
    </div>
  );
};

/**
 * Sommaire collant : une puce par sous-catégorie pour y sauter sans dérouler.
 *
 * La cible est le bloc de la colonne de GAUCHE, avec repli sur celle de droite
 * quand la sous-catégorie n'existe que là. Les deux tableaux n'ont pas le même
 * nombre de lignes : ils ne peuvent pas être alignés au pixel, on amène donc à
 * l'écran le bloc de gauche et celui de droite se lit en vis-à-vis.
 */
const NavBar = ({ nav }) => {
  const jump = (entry) => {
    const target =
      (entry.idA && document.getElementById(entry.idA)) ||
      (entry.idB && document.getElementById(entry.idB));
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        background: "#fff",
        borderBottom: "1px solid #eee",
        padding: ".5rem .25rem",
        marginBottom: "1rem",
        display: "flex",
        alignItems: "center",
        gap: ".35rem",
        // Une seule ligne : au-delà de la largeur disponible le sommaire défile
        // horizontalement plutôt que de passer sur deux lignes et de repousser
        // les tableaux vers le bas.
        overflowX: "auto",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".04em", color: "#aaa" }}>
        Aller à
      </span>
      {nav.map((entry) => (
        <Button
          key={entry.name}
          size="sm"
          variant="outline-secondary"
          style={{ padding: ".1rem .5rem", fontSize: ".8rem", flexShrink: 0 }}
          onClick={() => jump(entry)}
        >
          {entry.name}
        </Button>
      ))}
    </div>
  );
};

/**
 * Une colonne = un site, avec exactement le même tableau que l'autre.
 *
 * Les listes sont lues en `(x || [])` : une seule propriété absente du payload
 * (API plus ancienne que ce front, champ renommé) levait une exception en plein
 * rendu, et la colonne entière disparaissait SANS message — pas même le « aucun
 * produit ne correspond », qui aurait au moins indiqué où chercher.
 */
const SiteColumn = ({ side, name, counts, sections, diffKeys }) => (
  <div style={{ flex: "1 1 0", minWidth: 0 }}>
    <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap", marginBottom: ".5rem" }}>
      <h5 style={{ margin: 0, fontSize: "1rem", color: "#429853" }}>{name}</h5>
      {counts && (
        <>
          <Badge variant="success">{counts.orderable} en carte</Badge>
          <Badge variant="warning">{counts.soldOut} en rupture</Badge>
          <Badge variant="secondary">
            {counts.hidden} absent{counts.hidden > 1 ? "s" : ""}
          </Badge>
        </>
      )}
    </div>

    {!(sections || []).length ? (
      <Alert variant="light">Aucun produit ne correspond à ce filtre.</Alert>
    ) : (
      // Le `scrollMarginTop` de la section ci-dessous évite que le sommaire
      // collant recouvre le titre qu'on vient d'atteindre.
      //
      // NE PAS remettre ce commentaire entre `=> (` et la balise : Babel place
      // son annotation /*#__PURE__*/ avant les commentaires, Terser perd le lien
      // avec l'appel createElement, juge la valeur de retour inutilisée et
      // compile la fonction VIDE. Le dev n'y voit rien (pas de minification) et
      // la production affiche une colonne sans aucune ligne.
      sections.map((section) => (
        <section
          key={section.id}
          id={anchorId(side, section.id)}
          style={{ marginBottom: "1.25rem", scrollMarginTop: "4rem" }}
        >
          <h6 style={{ fontSize: ".8rem", textTransform: "uppercase", letterSpacing: ".03em", color: "#777" }}>
            {section.name} {!section.visible && <Badge variant="secondary">Section non affichée</Badge>}
          </h6>

          {(section.categories || []).map((category) => (
            <div key={category.id} style={{ marginBottom: ".75rem", background: "#fff", borderRadius: 6, boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
              <div style={{ padding: ".5rem .75rem", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
                <strong style={{ color: "#429853" }}>{category.name}</strong>
                {!category.visible && (
                  <>
                    <Badge variant="secondary">Catégorie non affichée</Badge>
                    {(category.reasons || []).length > 0 && (
                      <span style={{ fontSize: ".8rem", color: "#888" }}>{reasonsLabel(category.reasons)}</span>
                    )}
                  </>
                )}
              </div>

              <Table responsive size="sm" style={{ marginBottom: 0 }}>
                <thead>
                  <tr style={{ fontSize: ".75rem", textTransform: "uppercase", color: "#888" }}>
                    <th style={{ width: "3.5rem" }} />
                    <th>Produit</th>
                    <th style={{ width: "5rem" }}>Prix</th>
                    <th style={{ width: "5.5rem" }} title="Quantité déjà commandée sur le stock du jour">
                      Vte/stock
                    </th>
                    {/* Plus large que le seul badge : la colonne porte aussi la
                        raison des lignes non affichées, sur plusieurs lignes. */}
                    <th style={{ width: "11rem" }}>Statut</th>
                  </tr>
                </thead>
                {(category.blocks || []).map((block) => (
                  <tbody key={block.key}>
                    {/* Titre de sous-catégorie : un bloc par sous-catégorie, dans
                        l'ordre du BO — le même empilement que la carte du client.
                        Les produits rattachés directement à la catégorie n'ont pas
                        de titre (bloc « direct »), comme dans le tunnel. */}
                    {block.name && (
                      <tr>
                        <td
                          colSpan={5}
                          style={{ background: "#fafafa", fontSize: ".75rem", textTransform: "uppercase", letterSpacing: ".04em", color: "#777", fontWeight: 600 }}
                        >
                          {block.name}
                        </td>
                      </tr>
                    )}
                    {(block.rows || []).map((row) => {
                      const differs = diffKeys.has(rowKey(row));
                      return (
                        <tr
                          key={`${row.kind}-${row.id}-${row.suggestionIndex !== undefined ? row.suggestionIndex : "p"}`}
                          // Cible du clic depuis le récap des écarts.
                          // scrollMarginTop : le sommaire collant recouvrirait
                          // sinon la ligne qu'on vient d'atteindre.
                          id={rowAnchorId(side, category.name, row)}
                          style={{
                            opacity: row.status === "hidden" ? 0.6 : 1,
                            background: differs ? DIFF_BACKGROUND : undefined,
                            scrollMarginTop: "5rem",
                          }}
                        >
                          {/* Marge à droite de la vignette : sans elle, l'image
                              colle au nom du produit. */}
                          <td style={{ paddingRight: "1rem" }}>
                            {row.photo ? (
                              <img src={row.photo} alt="" loading="lazy" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }} />
                            ) : (
                              <div style={{ width: 40, height: 40, borderRadius: 4, background: "#f1f1f1" }} />
                            )}
                          </td>
                          <td>
                            {/* Nouvel onglet : on quitterait sinon la comparaison
                                en cours (contexte, filtres et défilement perdus)
                                pour un aller-retour vers le BO. */}
                            <a
                              href={productEditUrl(row.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={
                                row.kind === "suggestion"
                                  ? `Fiche du produit sur-mesure parent : ${row.parentName}`
                                  : "Ouvrir la fiche établissement (activation, prix, stock)"
                              }
                              style={{ color: "#212529", textDecoration: "underline", textDecorationStyle: "dotted" }}
                            >
                              <strong>{row.name}</strong>
                            </a>
                            {row.kind === "suggestion" && (
                              <Badge variant="info" style={{ marginLeft: ".4rem" }}>
                                Suggestion
                              </Badge>
                            )}
                            {differs && (
                              <Badge variant="danger" style={{ marginLeft: ".4rem" }}>
                                Écart
                              </Badge>
                            )}
                            {/* Second niveau : ce qui vaut pour tous les sites
                                (activation globale, jours de disponibilité). */}
                            {row.genericId && (
                              <a
                                href={genericEditUrl(row.genericId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Ouvrir la fiche du produit générique (tous sites)"
                                style={{ marginLeft: ".4rem", fontSize: ".7rem", color: "#999" }}
                              >
                                gén.
                              </a>
                            )}
                          </td>
                          <td style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                            {priceLabel(row.priceCents)}
                          </td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>{stockLabel(row)}</td>
                          {/* La raison sous le statut, pour les seules lignes
                              absentes de la carte : c'est là qu'on se demande
                              « pourquoi ». Une ligne en carte ou en rupture n'a
                              rien à expliquer, et l'écrire alourdirait la
                              colonne sur l'immense majorité des lignes. */}
                          <td>
                            <Badge variant={STATUS_VARIANTS[row.status]}>{STATUS_LABELS[row.status]}</Badge>
                            {row.status === "hidden" && row.reasons && row.reasons.length > 0 && (
                              <div style={{ marginTop: ".15rem", fontSize: ".75rem", color: "#888", whiteSpace: "normal", lineHeight: 1.25 }}>
                                {reasonsLabel(row.reasons)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </Table>
            </div>
          ))}
        </section>
      ))
    )}
  </div>
);

const FieldLabel = ({ children }) => (
  <div style={{ fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".04em", color: "#888", marginBottom: ".25rem" }}>
    {children}
  </div>
);

CartePreview.propTypes = {
  /** Établissement courant du BO (session) : sert de site de gauche par défaut. */
  establishment: PropTypes.number,
};

CartePreview.defaultProps = {
  establishment: null,
};

export default CartePreview;
